import React, { useEffect, useState } from "react"
import { Pencil, ChevronRight, ChevronDown, Plus, Save, X } from "lucide-react"
import { useAppCtx, appStore } from "lib/state";
import { cn, randoms } from "lib/utils";
import { findById, updateById, save } from "lib/utils/apiHelpers"
import { CollectionLoader, Dropdown, FieldGroup, FormLabel, InputField, Textarea } from "lib/components"
import { ELEMENTS, RULES_ACTIONS, sortArrayOfObjects } from "lib/utils"
import { ReplaceRuleFields, useRulesEngineCtx } from ".";

const operators = [
  { label: "Must Equal", value: "eq" },
  { label: "Must Not Equal", value: "neq" },
  { label: "Must Contain", value: "contains" },
  { label: "Must Not Contain", value: "notcontains" },
  { label: "Must Exist", value: "exists" },
  { label: "Must Not Exist", value: "notexists" },
]

export default function RuleBuilder({ updateRules }: { updateRules?: (changedRule:any) => void }) {
  const { engineState, updateEngineState } = useRulesEngineCtx();
  const [ newRuleAction, setNewRuleAction ] = useState<any>({ target: "", action: "" });

  const updateRule = async(id:string, data:any) => await updateById({ resource: "rule", idObj: { key: "_id", value: id }, data })

  const getRulesEligibleFields = () => {
    if( !ELEMENTS ) return []
    const elements:any[] = ELEMENTS
    const elemConfig:any = elements.find( (elem:any) => elem.refstr === engineState.selectedSection.refstr ).config
    const fields = elemConfig.structure.filter( (item:any) => item.includeInRules === true).map( (item:any) => ({ label: item.label, value: item.refstr }) )
    const alsoInclude = elemConfig.alsoIncludeInRules || []
    return sortArrayOfObjects([...fields, ...alsoInclude], "label")
  }
  
  const handleRuleFormChange = (e:any) => {
    if( e.target.name.startsWith("config:") ){
      updateEngineState("selectedRule.rule.config", { ...engineState.selectedRule.rule.config, [e.target.name.replace("config:", "")]: e.target.value })
    } else if( e.target.name.startsWith("action:") ){
      const editProp = e.target.name.split(":")[1]
      const refstr = e.target.name.split(":")[2]
      const newActions = engineState.selectedRule.rule.actions.map( (act:any) => { 
        if( act.refstr === refstr ){
          return { ...act, [editProp]: e.target.value }
        }
        return act
      })
      updateEngineState("selectedRule.rule.actions", newActions)
    } else {
      updateEngineState(`selectedRule.${e.target.name}`, e.target.value)
    }
  }

  const addNewAction = (e:any) => {
    updateEngineState(
      "selectedRule.rule.actions", 
      [
        ...engineState.selectedRule.rule.actions, 
        { refstr: randoms.string(10, "a#"), target: newRuleAction.target.slice(0), action: newRuleAction.action.slice(0) }
      ]
    );
    setNewRuleAction({ target: "", action: "" })
  }

  const saveSelectedRule = () => {
    const newRule = {
      _id: engineState.selectedRule._id || undefined,
      location: engineState.selectedRule.location,
      subdomain: engineState.selectedRule.subdomain || "-any-",
      rpid: engineState.selectedRule.rpid || "-any-",
      specificity: 0,
      rule: {
        refstr: engineState.selectedSection.refstr,
        name: engineState.selectedRule.rule.name || "New Rule",
        description: engineState.selectedRule.rule.description || "No description provided",
        config: { ...engineState.selectedRule.rule.config },
        actions: engineState.selectedRule.rule.actions || []
      }
    }
    const idObj = newRule._id ? { key: "_id", value: newRule._id } : undefined;
    save({ resource: "rule", idObj, data: newRule })
  }

  useEffect(() => {
    console.log("Selected Rule Changed", engineState.selectedRule);
  },[engineState.selectedRule])


  return (
    <div className="w-full flex flex-col">     
      <div className="flex items-center px-2 py-1 bg-slate-800 border-b border-solid border-slate-700 text-sm">
        Build/Edit A Rule
      </div>
              
      <div className="w-full flex flex-col px-4 py-2">
        <FieldGroup className="w-full mb-3 text-xs" required={true}>
          <FormLabel className="text-xs mb-[-5px] uppercase">Name:</FormLabel>
          <InputField className="text-xs" name="name" placeholder="Name" value={engineState.selectedRule.rule.name} onChange={handleRuleFormChange} />
        </FieldGroup>

        <FieldGroup className="w-full mb-3" required={true}>
          <FormLabel className="text-xs mb-[-5px] uppercase">Description:</FormLabel>
          <Textarea className="text-xs" name="description" placeholder="Description" value={engineState.selectedRule.rule.description} onChange={handleRuleFormChange} />
        </FieldGroup>

        <div className="w-full flex justify-start items-start gap-3">
          <FieldGroup className="w-full mb-3" required={true}>
            <FormLabel className="text-xs mb-[-5px] uppercase">Subdomain:</FormLabel>
            <InputField className="text-xs" name="subdomain" placeholder="Subdomain" value={engineState.selectedRule.subdomain} onChange={handleRuleFormChange} />
          </FieldGroup>
          <FieldGroup className="w-4/12 mb-3" required={true}>
            <FormLabel className="text-xs mb-[-5px] uppercase">RP:</FormLabel>
            <InputField className="text-xs" name="rpid" placeholder="rpid" value={engineState.selectedRpid} onChange={handleRuleFormChange} />
          </FieldGroup>
        </div>

        <div className="w-full flex flex-col p-3 border border-solid border-slate-500">

          <FieldGroup className="w-full mb-2" required={true}>
            <FormLabel className="text-xs mb-[-5px] uppercase">Data to Lookup as Template Loads:</FormLabel>
            <InputField className="text-xs" name="config:lookup" placeholder="ex: rp.Chargeback.Date" value={engineState.selectedRule.rule.config.lookup || ""} onChange={handleRuleFormChange} />
          </FieldGroup>

          <div className="flex w-full mt-2 gap-3">

            <div className="flex flex-col w-6/12">
              <FormLabel className="mb-1 pl-0 text-xs uppercase">Data Element Above:</FormLabel>
              <Dropdown
                className="w-full text-xs bg-slate-900"
                title="Operator"
                placeholder="Data Must"
                items={operators}
                selected={operators.find( (op:any) => op.value === engineState.selectedRule.rule.config.operator )?.label || ""}
                selectFn={(item:any) => {
                  handleRuleFormChange({ target: { name: "config:operator", value: item.value }} ) 
                }}
              />
            </div>

            <div className="w-6/12 flex flex-col">
              <FormLabel className="mb-1 pl-0 text-xs uppercase">This Value:</FormLabel>
              <InputField 
                className="text-xs" 
                name="config:value" 
                placeholder="ex: Miami Vice" 
                value={engineState.selectedRule.rule.config.value || ""} 
                onChange={handleRuleFormChange} 
              />
            </div>

          </div>
        </div>

        <p className="text-xs my-4 px-1">Choose which parts of the dispute template will be affected if a match is found:</p>

        <div className="w-full flex flex-col gap-1">
          { engineState.selectedRule.rule.actions !== undefined && engineState.selectedRule?.rule.actions.map( (action:any) => (
            
            <div className="w-full flex flex-col border border-solid border-slate-500" key={action.refstr}>
              <div key={action.target} className="w-full flex flex-row gap-3 p-[4px]">
                <div className="flex flex-col w-6/12">
                  {/* <FormLabel className="mb-1 pl-0 text-xs uppercase">Element:</FormLabel> */}
                  <Dropdown
                    className={cn(
                      "w-full text-xs bg-slate-900 border border-dotted border-slate-600",
                      action.action === "replace" && "opacity-25"
                    )}
                    title="Operator"
                    placeholder="Element"
                    items={getRulesEligibleFields()}
                    selected={getRulesEligibleFields().find( (op:any) => op.value === action.target )?.label || ""}
                    selectFn={(item:any) => {
                      handleRuleFormChange({ target: { name: `action:target:${action.refstr}`, value: item.value }} ) 
                    }}
                  />
                </div>

                <div className="flex flex-col w-4/12">
                  {/* <FormLabel className="mb-1 pl-0 text-xs uppercase">Action:</FormLabel> */}
                  <Dropdown
                    className={cn(
                      "w-full text-xs bg-slate-900 border border-dotted border-slate-600",
                      action.action === "replace" && "opacity-25"
                    )}
                    title="Action"
                    placeholder="Do This"
                    items={RULES_ACTIONS}
                    selected={RULES_ACTIONS.find( (op:any) => op.value === action.action )?.label || ""}
                    selectFn={(item:any) => {
                      handleRuleFormChange({ target: { name: `action:action:${action.refstr}`, value: item.value }} ) 
                    }}
                  />
                </div>

                <div className="flex h-full justify-start items-start w-2/12 gap-2 pt-[.5rem]">
                  { action.action !== undefined && action.action !== "hide" && (
                    <Pencil className="w-5 h-5 stroke-1 cursor-default" />
                  )}
                    <X 
                      className="flex items-center w-5 h-5 stroke-1 cursor-pointer stroke-white" 
                      onClick={() => updateEngineState("selectedRule.rule.actions", engineState.selectedRule.rule.actions.filter( (act:any) => act.refstr !== action.refstr ))} 
                    />
                </div>

              </div>

              { action.action === "replace" && <ReplaceRuleFields action={action} handleRuleFormChange={handleRuleFormChange} /> }
       
            </div>
          ))}

          <div className="w-full flex flex-row gap-3 mt-6">
            <div className="flex flex-col w-full">
              <FormLabel className="mb-1 pl-0 text-xs uppercase">New Element:</FormLabel>
              <Dropdown
                className="w-full text-xs bg-slate-900"
                title="Operator"
                placeholder="Element"
                items={getRulesEligibleFields()}
                selected={newRuleAction.target || ""}
                selectFn={(item:any) => setNewRuleAction( (prev:any) => ({ ...prev, target: item.value }) ) }
              />
            </div>

            <div className="flex flex-col w-full">
              <FormLabel className="mb-1 pl-0 text-xs uppercase">New Action:</FormLabel>
              <Dropdown
                className="w-full text-xs bg-slate-900"
                title="Action"
                placeholder="Do This"
                items={RULES_ACTIONS}
                selected={ RULES_ACTIONS.find( (op:any) => op.value === newRuleAction.action )?.label || ""}
                selectFn={(item:any) => {
                  console.log(item)
                  setNewRuleAction( (prev:any) => ({ ...prev, action: item.value }) ) }
                }
              />
            </div>

            <div className="flex h-full justify-center items-start w-2/12 gap-2 pt-[1.95rem]">
              { engineState.newRuleTarget.target.length !== 0 && engineState.newRuleTarget.action !== "hide" && (
                <Pencil className="w-5 h-5 stroke-1 cursor-default" />
              )}
              <Plus 
                className="flex items-center w-5 h-5 stroke-1 cursor-pointer stroke-white" 
                onClick={addNewAction} 
              />
            </div>
          </div>

        </div>

        <div className="w-full flex justify-center items-center gap-2 mt-4">
          <button 
            className="w-full px-3 py-1 bg-teal-800 text-white rounded-sm text-xs uppercase flex justify-center items-center gap-1"
            onClick={saveSelectedRule}
          >
            Save Rule
          </button>
        </div>

      </div>
    </div>
  );
}